import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  ChartFilter,
  ChartInput
} from '../frequency-view-chart-filters-container/frequency-view-chart-filters-container.component';
import {of, Subject} from 'rxjs';
import {ElasticValidator, ElasticValidatorFactory} from 'asax-elastic';
import {debounceTime, mergeAll, tap} from 'rxjs/operators';
import {chartInputDebounceTime} from '../frequency-view-chart-filter-lines/frequency-view-chart-filter-lines.component';

export type RedshiftMode = 'manual' | 'estimated';

@Component({
  selector: 'asax-frequency-view-frequency-view-chart-filter-redshift',
  templateUrl: './frequency-view-chart-filter-redshift.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequencyViewChartFilterRedshiftComponent implements OnInit, OnChanges {
  @Input() value: any;
  @Input() redshiftEstimated: number;
  @Input() chartInput: ChartInput;

  @Output() outFilter = new EventEmitter<ChartFilter>();
  @Output() outRedshiftMode = new EventEmitter<RedshiftMode>();

  isInvalid: boolean;
  errorMessage: string;

  valueSubject = new Subject<string>();
  valueSubjectWithDebounceTime = new Subject<string>();
  elasticValidator: ElasticValidator;

  redshiftMode: RedshiftMode = 'estimated';

  tooltipRedshiftMode = {
    estimated: 'The PIs are providing redshift information for the science targets when submitting their proposals.The median value of those redshift values for the sources listed in the Observation-tab result-table are used. If no redshift values is provided, redshift 0 is assumed. Note that there are cases (multiple galaxies in the same field, lensed objects, spectral scans, ...) where the redshifts provided by the PIs do not necessarily correspond to the true redshift of the sources.',
    manual: 'The estimated redshift can be overridden by specifying explicitly a redshift manually.'
  };

  constructor(private readonly elasticValidatorFactory: ElasticValidatorFactory,
              private readonly ref: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.emitRedshiftMode();

    this.elasticValidator = this.elasticValidatorFactory.getElasticValidator(this.chartInput.elasticValidatorType);

    of(
      this.valueSubjectWithDebounceTime.asObservable().pipe(debounceTime(chartInputDebounceTime)),
      this.valueSubject.asObservable()
    ).pipe(
      mergeAll(),
      tap(() => this.validateValue()),
      tap(() => this.emitFilter())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value && this.value !== undefined && this.value != null) {
      this.redshiftMode = 'manual';
      this.emitRedshiftMode();
      this.valueChanged();
    } else if (changes.redshiftEstimated && this.redshiftEstimated !== undefined && this.redshiftEstimated != null) {
      this.redshiftEstimatedChanged();
    }
  }

  valueChangedWithDebounceTime(): void {
    this.valueSubjectWithDebounceTime.next(undefined);
  }

  valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  validateValue(): void {
    this.isInvalid = undefined;

    if (this.value?.length && this.elasticValidator) {
      const {isInvalid, errorMessage} = this.elasticValidator.validate(this.value);
      this.isInvalid = isInvalid;
      this.errorMessage = errorMessage;
    }

    this.ref.markForCheck();
  }

  clearChartInputValue(): void {
    this.value = '';
    this.valueChanged();
  }

  onExampleClick(example: string): void {
    this.value = example;
    this.valueChanged();
  }

  setRedshiftMode(redshiftMode: RedshiftMode): void {
    this.redshiftMode = redshiftMode;
    this.emitRedshiftMode();

    if (this.redshiftMode === 'estimated') {
      this.value = this.redshiftEstimated;
    }

    this.valueChanged();
  }

  emitFilter(): void {
    const filter = {
      id: this.chartInput.id,
      value: Number(this.value),
      valueParsed:  Number(this.value),
      valueToDisplay: this.value,
      label: this.chartInput.label,
      isInvalid: this.isInvalid,
      elasticParserType: this.chartInput.elasticParserType
    };

    this.outFilter.emit(filter);
  }

  emitRedshiftMode(): void {
    this.outRedshiftMode.emit(this.redshiftMode);
  }

  private redshiftEstimatedChanged(): void {
    if (this.redshiftMode === 'estimated') {
      this.value = this.redshiftEstimated;
      this.valueChanged();
    }
  }
}
